/**
 * Agents Tab - Agent Configuration Editor
 * Master-detail view for editing AI agent configs (top-level agents map).
 * Only shown when network is deployed (ACTIVE).
 */
import React, { useCallback } from 'react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import AgentConfigEditor from './AgentConfigEditor';
import { useFieldLock } from '@/hooks/useFieldLock';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

export default function AgentsTab({ network, onAgentUpdate, workbenchEvents = [], onNavigateToConnections = null, pendingToolAssign = null, onPendingToolAssignConsumed = null }) {
  const { user, activeTenantId, workspaceRole } = useAuth();
  const agents = network?.agents || {};

  // Collaborative field locks — only meaningful for DRAFT/ACTIVE networks
  // that are still editable. Callers that pass an empty ``workbenchEvents``
  // array still get hydrated initial locks via the REST snapshot.
  const fieldLock = useFieldLock(network?.networkId, workbenchEvents);
  const canForceRelease = String(workspaceRole || '').toUpperCase() === 'OWNER'
    || String(workspaceRole || '').toUpperCase() === 'ADMIN';

  const handleSave = useCallback(async (networkId, logicalId, agentConfig) => {
    const idToken = getRestApiBearerToken();
    if (!idToken) throw new Error('Not authenticated');

    const res = await fetch(`${API_BASE_URL}/networks/${networkId}/agents/${logicalId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(agentConfig)
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || err?.error || `Save failed: ${res.status}`);
    }

    const updatedAgents = { ...(network?.agents || {}), [logicalId]: agentConfig };
    onAgentUpdate(updatedAgents);
    // Broadcast so sibling surfaces (Connections tab, room drawer) that read this agent's tool
    // assignment (``enabledMcpIds`` / tools) reflect the change immediately — it's the same source
    // row they operate on. Same event the connections/drawer edits use; listeners just re-fetch.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('stepflow-secret-registry-changed', { detail: { source: 'agent-config' } }));
    }
  }, [user, network?.agents, onAgentUpdate]);

  if (Object.keys(agents).length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center text-gray-400">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No agents configured</h3>
          <p className="text-sm text-gray-600">
            Deploy your network first. After deployment, agents will appear here for configuration.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
      <AgentConfigEditor
        agents={agents}
        networkId={network.networkId}
        tenantId={activeTenantId}
        onSave={handleSave}
        fieldLock={fieldLock}
        currentUserSub={user?.sub || ''}
        canForceRelease={canForceRelease}
        topologyGraph={network?.latestGraph ?? null}
        generative={String(network?.runtimeConfig?.engine || '').toLowerCase() === 'manifest'}
        engineTierModels={network?.engineTierModels ?? null}
        onNavigateToConnections={onNavigateToConnections}
        pendingToolAssign={pendingToolAssign}
        onPendingToolAssignConsumed={onPendingToolAssignConsumed}
      />
    </div>
  );
}
